/**
 * Shared, dependency-free helpers for detecting a Carfax "Risk Alert" rejection
 * from the vehicle service.
 *
 * A flagged vehicle cannot be published in the sell-to-dealer channel. The
 * vehicle service surfaces this both when looking up the vehicle and when
 * creating the assignment, and we don't fully control the exact error envelope,
 * so the detection below is tolerant of the shapes we've seen / expect:
 *
 *   { "error":  { "code": "RISK_ALERT", "source": "CARFAX", "message": "..." } }
 *   { "code":   "RISK_ALERT", "message": "..." }
 *   { "errors": [{ "code": "RISK_ALERT", ... }] }
 *
 * Kept free of server-only imports so both API route handlers and client
 * components can share it.
 */

/** Error code returned by the vehicle service when a vehicle is flagged. */
export const RISK_ALERT_ERROR_CODE = 'RISK_ALERT';

/** Status code we expose to the client for a flagged (rejected) vehicle. */
export const RISK_ALERT_STATUS = 422;

/** Normalized shape the client can rely on once the error is forwarded. */
export interface RiskAlertErrorBody {
  error: {
    code: string;
    source?: string;
    message?: string;
  };
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/** True when an object carries the risk-alert code under a `code`/`errorCode` key. */
const hasRiskAlertCode = (value: unknown): boolean =>
  isRecord(value) &&
  (value.code === RISK_ALERT_ERROR_CODE || value.errorCode === RISK_ALERT_ERROR_CODE);

/**
 * Returns true when the error body represents a Carfax Risk Alert rejection,
 * regardless of which of the known envelopes the service used.
 */
export const isRiskAlertErrorBody = (body: unknown): boolean => {
  if (!isRecord(body)) return false;
  // { code: 'RISK_ALERT', ... }
  if (hasRiskAlertCode(body)) return true;
  // { error: { code: 'RISK_ALERT', ... } }
  if (hasRiskAlertCode(body.error)) return true;
  // { errors: [{ code: 'RISK_ALERT', ... }] }
  if (Array.isArray(body.errors) && body.errors.some(hasRiskAlertCode)) return true;
  return false;
};
